import { BAOCustomElement } from '@bao/custom-element'
import { sectionRenderer } from '@bao/section-rendering'

class ProductCard extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    /* Update Stoq if product card has been dynamically re-rendered */
    if (this.dataset.updated) {
      window._RestockRocket.updateAddToCartForms(this.form)
    }

    this.setupObservers()
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.colour?.exists) {
      this.els.colour.elements.forEach((colourEl) => {
        this.listeners.add(
          colourEl,
          'pointerenter',
          this.updateColourImage.bind(this),
        )

        this.listeners.add(
          colourEl,
          'click',
          this.updateProductCard.bind(this),
        )
      })
    }

    if (this.els.coloursInner?.exists) {
      this.listeners.add(
        this.els.coloursInner.element,
        'pointerleave',
        this.hideColourImages.bind(this),
      )
    }

    if (this.els.currentColour?.exists) {
      this.listeners.add(
        this.els.currentColour.element,
        'pointerenter',
        this.hideColourImages.bind(this),
      )
    }
  }

  setupObservers () {
    if (this.coloursLoaded) return
    if (!this.productUrl) return

    this.onEntry = this.onEntry.bind(this)

    this.observer = new IntersectionObserver(this.onEntry, {
      threshold: 0,
    })

    this.observer.observe(this)
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    observer.unobserve(this)

    this.fetchProductColours()
  }

  async fetchProductColours () {
    try {
      const response = await sectionRenderer.fetchSingle(
        'product-card',
        this.productUrl,
      )

      const newDOM = sectionRenderer.convertToDOM(response)

      const coloursInner = newDOM.querySelector(
        '[data-product-card-el="coloursInner"]',
      )

      const colourImagesInner = newDOM.querySelector(
        '[data-product-card-el="colourImagesInner"]',
      )

      if (!coloursInner) {
        this.coloursLoaded = true
        this.setupListeners()
        return
      }

      this.els.colours.element.innerHTML = coloursInner.outerHTML
      this.els.colourImages.element.innerHTML = colourImagesInner.outerHTML

      /* Slight delay before updating attribute so transition happens correctly */
      setTimeout(() => {
        this.coloursLoaded = true
        this.setupListeners()
      }, 50)
    } catch (error) {
      console.error(error)
    }
  }

  updateColourImage (event) {
    const id = event.target.dataset.id
    if (!id) return

    if (this.els.colourImage?.exists) {
      this.els.colourImage.elements.forEach((colourImageEl) => {
        colourImageEl.setAttribute(
          'aria-hidden',
          colourImageEl.dataset.id !== id,
        )
      })
    }
  }

  hideColourImages () {
    if (this.els.colourImage?.exists) {
      this.els.colourImage.elements.forEach((colourImageEl) => {
        colourImageEl.setAttribute('aria-hidden', true)
      })
    }
  }

  updateProductCard (event) {
    const swatch = event.target.closest(this.els.colour.selector)
    if (!swatch) return
    const productUrl = swatch.dataset.productUrl
    if (!productUrl) return

    this.fetchProductCard(productUrl)
  }

  async fetchProductCard (productUrl) {
    try {
      const response = await sectionRenderer.fetchSingle(
        'product-card',
        productUrl,
      )

      const newDOM = sectionRenderer.convertToDOM(response)
      const myself = newDOM.querySelector(this.localName)

      /* Update existing colour swatches so positions remain consistent */
      const currentColoursEl = this.els.colours.element
      const newColoursEl = myself.querySelector(this.els.colours.selector)
      newColoursEl.outerHTML = currentColoursEl.outerHTML

      const newColourEls = [
        myself.querySelector(this.els.currentColour.selector),
        ...myself.querySelectorAll(this.els.colour.selector),
      ]

      newColourEls.forEach((newColourEl) => {
        const isActive = newColourEl.dataset.productUrl === productUrl
        newColourEl.classList.toggle('prd-Card_Colour-current', isActive)

        newColourEl.dataset.productCardEl = isActive
          ? 'currentColour'
          : 'colour'
      })

      myself.dataset.updated = true

      this.outerHTML = myself.outerHTML
    } catch (error) {
      console.error(error)
    }
  }

  get coloursLoaded () {
    return this.dataset.coloursLoaded === 'true'
  }

  set coloursLoaded (value) {
    this.dataset.coloursLoaded = value
  }

  /**
   * @returns {HTMLFormElement|null}
   */
  get form () {
    return this.querySelector('form')
  }

  get productUrl () {
    return this.dataset.productUrl || ''
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
